import { T as Methods, c as MAXIMUM_SCORE, l as MINIMUM_SCORE, o as ACADEMIC_YEAR_MIN, t as SlugSchema } from "./slug.schemas--_8HzbpI.js";
import * as z$1 from "zod";

//#region src/models/brin.schemas.ts
/**
* BRIN4 is a 4-character BRIN code.
* Also known as institution code. ("Instellingscode")
* It consists of 2 digits followed by 2 uppercase letters.
* @example "00BB"
* @example "12CD"
*/
const Brin4Schema = z$1.string().trim().toUpperCase().regex(/^\d{2}[A-Z]{2}$/, "Invalid BRIN4 format").brand().meta({
	name: "spark-brin",
	title: "BRIN4",
	description: "Represents a 4-character BRIN code (institution code) in the Dutch education system.",
	examples: ["00BB", "12CD"]
});
/**
* BRIN6 is a 6-character BRIN code.
* Also known as location code. ("Vestigingscode")
* It consists of 2 digits followed by 2 uppercase letters and 2 digits.
* @example "00BB01"
* @example "00FA43"
*/
const Brin6Schema = z$1.string().trim().toUpperCase().regex(/^\d{2}[A-Z]{2}\d{2}$/, "Invalid BRIN6 format").brand().meta({
	name: "spark-brin",
	title: "BRIN6",
	description: "Represents a 6-character BRIN code (location code) in the Dutch education system.",
	examples: ["00BB01", "00FA43"]
});
/**
* BRIN (Basis Registratie Instellingen en Nummering) is a Dutch
* registration number for educational institutions.
* It can be either a 4-character or 6-character code.
* - BRIN4: 4-character code (e.g., "00BB")
* - BRIN6: 6-character code (e.g., "00BB01")
* @example "00BB"
* @example "00BB01"
*/
const BrinSchema = z$1.union([Brin4Schema, Brin6Schema]).meta({
	name: "spark-brin",
	title: "BRIN",
	description: "Represents a BRIN code (either BRIN4 or BRIN6) in the Dutch education system.",
	examples: ["00BB", "00BB01"]
});

//#endregion
//#region src/models/release.schemas.ts
const EXAMPLES = [
	"bwg-34-t1b",
	"spelling-thema5-groep5",
	"marjan-verwijderen"
];
/**
* Publication tag, used to identify a release specifically for publication.
*
* @example "bwg-34-t1b"
* @example "spelling-thema5-groep5"
* @example "marjan-verwijderen"
*/
const PublicationTagDraftSchema = SlugSchema.brand().meta({
	name: "spark-publication-tag-draft",
	title: "Draft Publication Tag",
	description: "Draft publication tag, used to identify a release specifically for draft publications.",
	examples: EXAMPLES
});
/**
* Retracted publication tag, used to identify a release specifically for retracted publications.
*
* @example "bwg-34-t1b_depublished"
* @example "spelling-thema5-groep5_depublished"
* @example "marjan-verwijderen_depublished"
*/
const PublicationTagRetractedSchema = z$1.string().trim().toLowerCase().min(3).regex(/^([a-z0-9]+(-[a-z0-9]+)*)_depublished$/).brand().brand().meta({
	name: "spark-publication-tag-retracted",
	title: "Retracted Publication Tag",
	description: "Retracted publication tag, used to identify a release specifically for retracted publications.",
	examples: EXAMPLES.map((example) => `${example}_depublished`)
});
/**
* Draft publication tag, used to identify a release specifically for draft publications.
*
* @example "bwg-34-t1b"
* @example "spelling-thema5-groep5"
* @example "marjan-verwijderen"
* @example "bwg-34-t1b_depublished"
* @example "spelling-thema5-groep5_depublished"
* @example "marjan-verwijderen_depublished"
*/
const PublicationTagSchema = z$1.union([PublicationTagDraftSchema, PublicationTagRetractedSchema]).meta({
	name: "spark-publication-tag",
	title: "Publication Tag",
	description: "Publication tag, used to identify a release specifically for publications (draft or retracted).",
	examples: [...EXAMPLES, ...EXAMPLES.map((example) => `${example}_depublished`)]
});
/**
* Retraction tag, used to identify a release specifically for retraction.
*
* @example "bwg-34-t1b"
* @example "spelling-thema5-groep5"
* @example "marjan-verwijderen"
*/
const RetractionTagSchema = SlugSchema.brand().meta({
	name: "spark-retraction-tag",
	title: "Retraction Tag",
	description: "Retraction tag, used to identify a release specifically for retraction.",
	examples: EXAMPLES
});
/**
* Release tag  used to identify a release containing publications and/or retractions.
*
* @example "bwg-34-t1b"
* @example "spelling-thema5-groep5"
* @example "marjan-verwijderen"
*/
const ReleaseTagSchema = z$1.union([PublicationTagSchema, RetractionTagSchema]).meta({
	name: "spark-release-tag",
	title: "Release Tag",
	description: "Release tag  used to identify a release containing publications and/or retractions.",
	examples: EXAMPLES
});

//#endregion
//#region src/primitives/json-schema.ts
/**
* Override JSON Schema generation for schemas whose runtime output cannot be
* represented directly, such as codecs that produce class instances.
*/
function overrideJSONSchema(parser, customJSONSchema) {
	const { name, ...rest } = customJSONSchema;
	const schema = {
		...rest,
		name: name.replace(/-override$/, "")
	};
	parser._zod.toJSONSchema = () => schema;
	parser._zod.parent = void 0;
	return parser;
}

//#endregion
//#region src/primitives/timestamp.schemas.ts
/**
* Creates a date parser that:
* - Accepts Date objects or ISO datetime strings
* - Coerces to Date object at runtime
* - Outputs proper JSON Schema (string with date-time format) for Swagger
*
* Note: This will be fixed in the future in nest-zod or zodV4. It's a workaround for now.
*/
function zDateTime(meta) {
	return overrideJSONSchema(z$1.union([z$1.date(), z$1.union([z$1.iso.datetime(), z$1.iso.datetime({ precision: -1 })]).pipe(z$1.coerce.date())]).meta(meta), z$1.toJSONSchema(z$1.iso.datetime().meta({
		...meta,
		name: `${meta.name}-override`
	})));
}

//#endregion
//#region src/models/cms/timestamps.schemas.ts
/**
* CreatedAt timestamp for CMS entities.
*
* @example "2023-01-01T12:00:00.000Z"
*/
const CreatedAtSchema = zDateTime({
	name: "spark-created-at",
	title: "Created At",
	description: "The date and time when the entity was created.",
	examples: ["2023-01-01T12:00:00.000Z"]
});
/**
* UpdatedAt timestamp for CMS entities.
*
* @example "2023-01-01T12:23:00.000Z"
*/
const UpdatedAtSchema = zDateTime({
	name: "spark-updated-at",
	title: "Updated At",
	description: "The date and time when the entity was last updated.",
	examples: ["2023-01-01T12:23:00.000Z"]
});
/**
* CreatedAt and UpdatedAt timestamps for CMS entities.
*
* createdAt must be before or equal to updatedAt.
*
* @example { createdAt: "2023-01-01T12:00:00.000Z", updatedAt: "2023-01-01T12:23:00.000Z" }
*/
const TimestampsSchema = z$1.object({
	createdAt: CreatedAtSchema,
	updatedAt: UpdatedAtSchema
}).refine((data) => data.createdAt <= data.updatedAt, { message: "createdAt must be before or equal to updatedAt" }).meta({
	name: "spark-timestamps",
	title: "Timestamps",
	description: "Timestamps for creation and last update of an entity.",
	examples: [{
		createdAt: "2023-01-01T12:00:00.000Z",
		updatedAt: "2023-01-01T12:23:00.000Z"
	}]
});

//#endregion
//#region src/models/color.constants.ts
/**
* List of all CSS color names as defined in the CSS Color Module Level 4 specification.
* Source: https://www.w3.org/TR/css-color-4/#named-colors
*/
const COLORS = [
	"indianred",
	"lightcoral",
	"salmon",
	"darksalmon",
	"lightsalmon",
	"crimson",
	"red",
	"firebrick",
	"darkred",
	"pink",
	"lightpink",
	"hotpink",
	"deeppink",
	"mediumvioletred",
	"palevioletred",
	"lightsalmon",
	"coral",
	"tomato",
	"orangered",
	"darkorange",
	"orange",
	"gold",
	"yellow",
	"lightyellow",
	"lemonchiffon",
	"lightgoldenrodyellow",
	"papayawhip",
	"moccasin",
	"peachpuff",
	"palegoldenrod",
	"khaki",
	"darkkhaki",
	"lavender",
	"thistle",
	"plum",
	"violet",
	"orchid",
	"fuchsia",
	"magenta",
	"mediumorchid",
	"mediumpurple",
	"rebeccapurple",
	"blueviolet",
	"darkviolet",
	"darkorchid",
	"darkmagenta",
	"purple",
	"indigo",
	"slateblue",
	"darkslateblue",
	"mediumslateblue",
	"greenyellow",
	"chartreuse",
	"lawngreen",
	"lime",
	"limegreen",
	"palegreen",
	"lightgreen",
	"mediumspringgreen",
	"springgreen",
	"mediumseagreen",
	"seagreen",
	"forestgreen",
	"green",
	"darkgreen",
	"yellowgreen",
	"olivedrab",
	"olive",
	"darkolivegreen",
	"mediumaquamarine",
	"darkseagreen",
	"lightseagreen",
	"darkcyan",
	"teal",
	"aqua",
	"cyan",
	"lightcyan",
	"paleturquoise",
	"aquamarine",
	"turquoise",
	"mediumturquoise",
	"darkturquoise",
	"cadetblue",
	"steelblue",
	"lightsteelblue",
	"powderblue",
	"lightblue",
	"skyblue",
	"lightskyblue",
	"deepskyblue",
	"dodgerblue",
	"cornflowerblue",
	"mediumslateblue",
	"royalblue",
	"blue",
	"mediumblue",
	"darkblue",
	"navy",
	"midnightblue",
	"cornsilk",
	"blanchedalmond",
	"bisque",
	"navajowhite",
	"wheat",
	"burlywood",
	"tan",
	"rosybrown",
	"sandybrown",
	"goldenrod",
	"darkgoldenrod",
	"peru",
	"chocolate",
	"saddlebrown",
	"sienna",
	"brown",
	"maroon",
	"white",
	"snow",
	"honeydew",
	"mintcream",
	"azure",
	"aliceblue",
	"ghostwhite",
	"whitesmoke",
	"seashell",
	"beige",
	"oldlace",
	"floralwhite",
	"ivory",
	"antiquewhite",
	"linen",
	"lavenderblush",
	"mistyrose",
	"gainsboro",
	"lightgray",
	"silver",
	"darkgray",
	"gray",
	"dimgray",
	"lightslategray",
	"slategray",
	"darkslategray",
	"black"
];

//#endregion
//#region src/models/color.schemas.ts
/**
* A hex color code, starting with a # followed by either 3 or 6 hexadecimal characters.
*
* @example "#fff"
* @example "#123456"
*/
const HexColorSchema = z$1.stringFormat("hex-color", /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/).brand("hex-color").meta({
	id: "spark-hex-color",
	title: "Hex Color",
	description: "A hex color code, starting with a # followed by either 3 or 6 hexadecimal characters.",
	examples: ["#fff", "#123456"]
});
/**
* An RGB color code in the format rgb(r, g, b) where r, g, and b are integers between 0 and 255.
*
* @example "rgb(255, 0, 0)"
* @example "rgb(0, 255, 0)"
* @example "rgb(0, 0, 255)"
*/
const RgbColorSchema = z$1.stringFormat("rgb-color", /^rgb\(\s*(\d{1,3}\s*,\s*){2}\d{1,3}\s*\)$/).brand("rgb-color").meta({
	id: "spark-rgb-color",
	title: "RGB Color",
	description: "An RGB color code in the format rgb(r, g, b) where r, g, and b are integers between 0 and 255.",
	examples: [
		"rgb(255, 0, 0)",
		"rgb(0, 255, 0)",
		"rgb(0, 0, 255)"
	]
});
/**
* An HSL color code in the format hsl(h, s%, l%) where h is an integer between 0 and 360 representing the hue, and s and l are integers between 0 and 100 representing the saturation and lightness percentages.
*
* @example "hsl(0, 100%, 50%)"
* @example "hsl(120, 100%, 50%)"
* @example "hsl(240, 100%, 50%)"
*/
const HslColorSchema = z$1.stringFormat("hsl-color", /^hsl\(\s*(\d{1,3})\s*,\s*(\d{1,3})%\s*,\s*(\d{1,3})%\s*\)$/).brand("hsl-color").meta({
	id: "spark-hsl-color",
	title: "HSL Color",
	description: "An HSL color code in the format hsl(h, s%, l%) where h is an integer between 0 and 360 representing the hue, and s and l are integers between 0 and 100 representing the saturation and lightness percentages.",
	examples: [
		"hsl(0, 100%, 50%)",
		"hsl(120, 100%, 50%)",
		"hsl(240, 100%, 50%)"
	]
});
/**
* A CSS color name, such as "red", "blue", or "green". The full list of supported CSS color names can be found at https://developer.mozilla.org/en-US/docs/Web/CSS/color_value#color_keywords.
*
* @example "red"
* @example "blue"
* @example "green"
*/
const CssColorSchema = z$1.preprocess((value) => typeof value === "string" ? value.trim().toLowerCase() : value, z$1.enum(COLORS)).brand("css-color").meta({
	id: "spark-css-color",
	title: "CSS Color",
	description: "A CSS color name.",
	examples: COLORS
});
/**
* A color in either hex, rgb, hsl format or a CSS color name.
*
* @example "#fff"
* @example "rgb(255, 0, 0)"
* @example "hsl(0, 100%, 50%)"
* @example "red"
*/
const ColorSchema = z$1.union([
	HexColorSchema,
	RgbColorSchema,
	HslColorSchema,
	CssColorSchema
]).meta({
	id: "spark-color",
	title: "Color",
	description: "A color in either hex, rgb, hsl format or a CSS color name.",
	examples: [
		"#fff",
		"rgb(255, 0, 0)",
		"hsl(0, 100%, 50%)",
		"red"
	]
});

//#endregion
//#region src/models/grade.schemas.ts
/**
* aaa
*/
/**
* All grades in the Dutch education system.
**/
const GRADES = [
	"PO_1",
	"PO_2",
	"PO_3",
	"PO_4",
	"PO_5",
	"PO_6",
	"PO_7",
	"PO_8"
];
/**
* Represents a grade in the Dutch education system.
* Grades are defined as per the PO (Primair Onderwijs) system.
* @see https://duo.nl/zakelijk/primair-onderwijs/leerlingenadministratie/leerjaar-en-groep-uitwisselen.jsp
* @example "PO_1"
* @example "PO_4"
* @example "PO_8"
*/
const GradeSchema = z$1.preprocess((val) => {
	if (typeof val === "string") return val.trim().toUpperCase();
	return val;
}, z$1.enum(GRADES)).meta({
	name: "spark-grade",
	title: "Grade",
	description: "Represents a grade in the Dutch education system.",
	examples: GRADES
});
/**
* Represents a non empty set of grades in the Dutch education system.
* Grades are defined as per the PO (Primair Onderwijs) system.
* @see https://duo.nl/zakelijk/primair-onderwijs/leerlingenadministratie/leerjaar-en-groep-uitwisselen.jsp
* @example ["PO_1", "PO_4"]
* @example ["PO_2", "PO_5", "PO_8"]
*/
const GradesSchema = z$1.preprocess((val) => {
	let set;
	if (Array.isArray(val)) set = new Set(val);
	else if (val instanceof Set) set = val;
	else if (typeof val === "string") set = new Set(val.split(",").map((s) => s.trim().toUpperCase()));
	else return val;
	const array = Array.from(set);
	array.sort();
	return array;
}, z$1.array(GradeSchema).nonempty().refine((arr) => new Set(arr).size === arr.length, { message: "Grades must be unique" })).meta({
	name: "spark-grades",
	title: "Grades",
	description: "Represents a set of grades in the Dutch education system.",
	examples: [
		["PO_1", "PO_2"],
		[
			"PO_3",
			"PO_4",
			"PO_5"
		],
		["PO_7", "PO_8"]
	],
	uniqueItems: true
});

//#endregion
//#region src/models/internal-role.schemas.ts
/**
* Wildcard character for roles.
*/
const Wildcard = "*";
/**
* Spark method role.
* @example "Spark.method/taal-lezen/edit"
* @example "Spark.method/bwgeintegreerd/publish"
* @example "Spark.method/spelling-grammatica/*"
*/
const SparkMethodRoleSchema = z$1.templateLiteral([
	z$1.literal("Spark.method/"),
	z$1.enum([...Methods, Wildcard]),
	z$1.literal("/"),
	z$1.enum([
		"edit",
		"publish",
		Wildcard
	])
]).meta({
	id: "spark-internal-role-method",
	title: "Internal Role: Method",
	description: "Spark method role for system permissions.",
	examples: [
		"Spark.method/taal-lezen/edit",
		"Spark.method/bwgeintegreerd/publish",
		"Spark.method/spelling-grammatica/*",
		"Spark.method/*/*"
	]
});
/**
* Spark beheer role.
*/
const SparkBeheerRoleSchema = z$1.templateLiteral([
	z$1.literal("Spark.beheer/"),
	z$1.literal(Wildcard),
	z$1.literal("/"),
	z$1.enum([
		"read",
		"edit",
		Wildcard
	])
]).meta({
	id: "spark-internal-role-beheer",
	title: "Internal Role: Beheer",
	description: "Spark beheer role for system permissions.",
	examples: [
		"Spark.beheer/*/read",
		"Spark.beheer/*/edit",
		"Spark.beheer/*/*"
	]
});
/**
* Spark internal role for system permissions.
* @example "Spark.method/taal-lezen/edit"
* @example "Spark.method/bwgeintegreerd/publish"
* @example "Spark.method/spelling-grammatica/*"
*/
const InternalRoleSchema = z$1.union([SparkMethodRoleSchema, SparkBeheerRoleSchema]).meta({
	name: "spark-internal-role",
	title: "Internal Role",
	description: "Spark internal role for system permissions.",
	examples: [
		"Spark.method/taal-lezen/edit",
		"Spark.method/bwgeintegreerd/publish",
		"Spark.method/spelling-grammatica/*",
		"Spark.method/*/*",
		"Spark.beheer/*/read",
		"Spark.beheer/*/edit",
		"Spark.beheer/*/*"
	]
});

//#endregion
//#region src/models/score.schemas.ts
const ScoreSchema = z$1.number().min(0).max(100).meta({
	name: "spark-score",
	title: "Score",
	description: `A score awarded for an answer, from ${0} to ${100}.`,
	examples: [
		0,
		60,
		100
	]
});

//#endregion
//#region src/primitives/academic-year.schemas.ts
/**
* An academic year ("schooljaar"), named after the calendar year it starts in:
* academic year 2026 runs from 1 August 2026 up to and including 31 July 2027.
*
* Numeric strings are coerced so the value can arrive straight from a query
* parameter or a path segment.
* @example 2026
*/
const AcademicYearSchema = z$1.coerce.number().int().min(ACADEMIC_YEAR_MIN).brand().meta({
	name: "academic-year",
	title: "Academic Year",
	description: "An academic year, named after the calendar year it starts in. Academic year 2026 runs from 1 August 2026 up to and including 31 July 2027.",
	examples: [2025, 2026]
});

//#endregion
//#region src/primitives/boolean.schemas.ts
/**
* A boolean or a case-insensitive `"true"`/`"false"` string, parsed as a
* boolean.
*/
const BooleanSchema = z$1.codec(z$1.union([z$1.boolean(), z$1.string().trim().toLowerCase().pipe(z$1.enum(["true", "false"]))]), z$1.boolean(), {
	decode: (value) => typeof value === "boolean" ? value : value === "true",
	encode: (value) => value
}).meta({
	name: "boolean",
	title: "Boolean",
	description: "A boolean value, optionally represented by a case-insensitive true or false string.",
	examples: [true, false]
});

//#endregion
//#region src/primitives/email.schemas.ts
const META = {
	name: "email",
	title: "Email",
	description: "An email address.",
	examples: ["teacher@blink-dev.nl"]
};
/**
* An email address, lowercased.
*
* Trimming and lowercasing happen in a `preprocess` rather than as trailing
* `.trim()`/`.toLowerCase()` checks: zod runs format validation before those, so
* `" Teacher@Example.NL "` would be rejected before it was ever cleaned up.
*
* The inner schema carries the same metadata as the branded outer one so the
* generated JSON Schema keeps `format: "email"` and its examples on the input
* side as well as the output side.
* @example "teacher@blink-dev.nl"
*/
const EmailSchema = z$1.preprocess((value) => typeof value === "string" ? value.trim().toLowerCase() : value, z$1.email().meta(META)).brand().meta(META);

//#endregion
//#region src/primitives/number.schemas.ts
/** A positive integer, accepting numeric strings as input. */
const PositiveIntegerSchema = z$1.coerce.number().int().positive().meta({
	name: "positive-integer",
	title: "Positive Integer",
	description: "An integer greater than zero.",
	examples: [1, 42]
});
/** A positive integer, accepting numeric strings as input. */
const NonNegativeIntegerSchema = z$1.coerce.number().int().nonnegative().meta({
	name: "non-negative-integer",
	title: "Non-Negative Integer",
	description: "An integer greater than or equal to zero.",
	examples: [
		0,
		1,
		42
	]
});

//#endregion
//#region src/primitives/url.schemas.ts
const HTTP_PROTOCOL = /^https?$/;
const HTTP_URL_META = {
	name: "http-url",
	title: "HTTP URL",
	description: "An absolute HTTP or HTTPS URL.",
	examples: ["https://example.com/"]
};
const HttpUrlStringSchema = z$1.url({ protocol: HTTP_PROTOCOL });
/** An HTTP or HTTPS URL, parsed as a `URL` object. */
const HttpUrlSchema = overrideJSONSchema(z$1.codec(z$1.union([HttpUrlStringSchema, z$1.instanceof(URL).refine((url) => HTTP_PROTOCOL.test(url.protocol.replace(/:$/, "")), { error: "Invalid HTTP URL" })]), z$1.instanceof(URL), {
	decode: (value) => new URL(value),
	encode: (value) => value
}).meta(HTTP_URL_META), z$1.toJSONSchema(HttpUrlStringSchema.meta({
	...HTTP_URL_META,
	name: `${HTTP_URL_META.name}-override`
})));

//#endregion
//#region src/primitives/uuid.schemas.ts
/**
* A UUID (Universally Unique Identifier) version 4, represented as a string.
* @example "68c8c773-5eae-405d-a6f1-2d3d1c635e81"
*/
const UuidSchema = z$1.uuidv4().brand().meta({
	name: "uuid",
	title: "UUID",
	description: "A universally unique identifier (UUID) version 4.",
	examples: ["68c8c773-5eae-405d-a6f1-2d3d1c635e81"]
});

//#endregion
export { BrinSchema as A, PublicationTagDraftSchema as C, RetractionTagSchema as D, ReleaseTagSchema as E, Brin4Schema as O, zDateTime as S, PublicationTagSchema as T, RgbColorSchema as _, EmailSchema as a, TimestampsSchema as b, ScoreSchema as c, GradeSchema as d, GradesSchema as f, HslColorSchema as g, HexColorSchema as h, PositiveIntegerSchema as i, Brin6Schema as k, InternalRoleSchema as l, CssColorSchema as m, HttpUrlSchema as n, BooleanSchema as o, ColorSchema as p, NonNegativeIntegerSchema as r, AcademicYearSchema as s, UuidSchema as t, GRADES as u, COLORS as v, PublicationTagRetractedSchema as w, UpdatedAtSchema as x, CreatedAtSchema as y };